/* RESPONSIBLE TEAM: team-ai-insights */

export interface SummaryCard {
  summary_title: string;
  summary_text: string;
  conversation_ids?: number[];
}

export function convertMarkdownToHtml(
  markdown: string,
  includeLinks = true,
  includeExample = true,
) {
  let text = markdown;
  if (!text) {
    return '';
  }

  // Remove "customers said" section and its bullet points if includeExample is false
  if (!includeExample) {
    text = text.replace(/\*\*Key verbatims:\*\*\s*\n(?:- [^\n]*\n?)*/gi, '');
  }

  // Replace markdown-style links [text](id) with clickable elements or plain text
  text = text.replace(/\[([^\]]+)\]\((\d+)\)/g, (_match, linkText, conversationId) => {
    if (includeLinks) {
      return `<a href="#" class="text-blue-600 hover:text-blue-800 underline cursor-pointer" data-conversation-id="${conversationId}">${linkText}</a>`;
    } else {
      return linkText;
    }
  });

  // Convert markdown headers (####) to HTML h4 tags
  text = text.replace(/^#### (.+)$/gm, '<h4 class="text-lg font-semibold mt-4 mb-2">$1</h4>');

  // Convert markdown bold (**text**) to HTML strong tags
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Convert markdown italics (*text*) to HTML em tags
  text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Convert markdown bullet points to HTML list items
  text = text.replace(/^- (.+)$/gm, '<li class="ml-4 mb-2">$1</li>');

  // Wrap consecutive list items in ul tags
  text = text.replace(/(<li[^>]*>.*<\/li>)(\s*<li[^>]*>.*<\/li>)*/gs, (match) => {
    return `<ul class="list-disc pl-4 mb-2">${match}</ul>`;
  });

  // Remove newlines within ul elements, then convert remaining newlines to <br> tags
  text = text.replace(/<ul[^>]*>(.*?)<\/ul>/gs, (match, content) => {
    // Remove newlines within ul content
    let cleanedContent = content.replace(/\n/g, '');
    return match.replace(content, cleanedContent);
  });

  // Convert remaining markdown newlines to HTML br tags
  text = text.replace(/\n/g, '<br>');

  return text;
}

export function extractSummaryCards(markdownXml: string): Array<SummaryCard> {
  if (!markdownXml) {
    return [];
  }

  let summaryCards: Array<SummaryCard> = [];
  let summaryCardRegex = /<summary_card>([\s\S]*?)<\/summary_card>/g;

  let match;
  while ((match = summaryCardRegex.exec(markdownXml)) !== null) {
    let cardContent = match[1];
    let cardObject: Partial<SummaryCard> = {};

    // Extract inner tags and their content
    let innerTagRegex = /<(\w+)>([\s\S]*?)<\/\1>/g;
    let innerMatch;

    while ((innerMatch = innerTagRegex.exec(cardContent)) !== null) {
      let tagName = innerMatch[1];
      let tagContent = innerMatch[2].trim();

      if (tagName === 'conversation_ids') {
        // Parse conversation_ids as an array of numbers
        try {
          // Remove square brackets if present and parse as JSON array or comma-separated string
          let cleanContent = tagContent.replace(/^\[|\]$/g, '').trim();
          // Parse as comma-separated string
          cardObject.conversation_ids = cleanContent
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));
        } catch {
          cardObject.conversation_ids = [];
        }
      } else {
        (cardObject as any)[tagName] = tagContent;
      }
    }

    // Only add if we have the required fields
    if (cardObject.summary_title && cardObject.summary_text) {
      summaryCards.push(cardObject as SummaryCard);
    }
  }

  return summaryCards;
}
